package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.FileFolder
import co.uk.sacursoftware.ssinventory.data.model.SaveFileFolderRequest
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FileFolderManagerScreen(
    onBack: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    var folders by remember { mutableStateOf<List<FileFolder>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var folderName by remember { mutableStateOf("") }
    var subFolderName by remember { mutableStateOf("") }
    var isSaving by remember { mutableStateOf(false) }
    var folderToDelete by remember { mutableStateOf<FileFolder?>(null) }

    fun loadFolders() {
        scope.launch {
            isLoading = true
            repository.getFileFolders().onSuccess { folders = it }.onFailure { folders = emptyList() }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) { loadFolders() }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text("Manage file folders") },
                navigationIcon = {
                    IconButton(onClick = onBack) { BackArrowIcon() }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier.padding(padding).fillMaxSize().padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.medium,
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
            ) {
                Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text("Add folder / subfolder", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                    OutlinedTextField(
                        value = folderName,
                        onValueChange = { folderName = it },
                        label = { Text("Folder name *") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = MaterialTheme.shapes.small
                    )
                    OutlinedTextField(
                        value = subFolderName,
                        onValueChange = { subFolderName = it },
                        label = { Text("Subfolder name (optional)") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = MaterialTheme.shapes.small
                    )
                    Button(
                        onClick = {
                            if (folderName.isNotBlank()) {
                                val req = SaveFileFolderRequest(
                                    folderName = folderName.trim(),
                                    subFolderName = subFolderName.ifBlank { null }
                                )
                                scope.launch {
                                    isSaving = true
                                    repository.saveFileFolder(req).onSuccess {
                                        folderName = ""
                                        subFolderName = ""
                                        loadFolders()
                                        snackbarHostState.showSnackbar("Folder saved")
                                    }.onFailure {
                                        snackbarHostState.showSnackbar("Failed to save folder")
                                    }
                                    isSaving = false
                                }
                            }
                        },
                        enabled = folderName.isNotBlank() && !isSaving,
                        modifier = Modifier.align(Alignment.End),
                        shape = MaterialTheme.shapes.small
                    ) {
                        Text("Add folder")
                    }
                }
            }

            Text("Existing folders (${folders.size})", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

            if (isLoading) {
                Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                    BrandLoadingSpinner()
                }
            } else if (folders.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                    Text("No folders yet.", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.outline)
                }
            } else {
                LazyColumn(modifier = Modifier.fillMaxWidth().weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    items(items = folders, key = { it.id }) { folder ->
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            shape = MaterialTheme.shapes.small,
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(12.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                                    Text(folder.folderName, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                                    if (!folder.subFolderName.isNullOrBlank()) {
                                        Text(folder.subFolderName, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.primary)
                                    }
                                }
                                IconButton(onClick = { folderToDelete = folder }) {
                                    CloseIcon(tint = MaterialTheme.colorScheme.error)
                                }
                            }
                        }
                    }
                }
            }
        }

        folderToDelete?.let { folder ->
            ConfirmDeleteDialog(
                message = "Delete '${folder.folderName}${folder.subFolderName?.let { " -> $it" } ?: ""}'? Files in this folder will also be deleted.",
                onConfirm = {
                    val id = folder.id
                    folderToDelete = null
                    folders = folders.filter { it.id != id }
                    scope.launch { repository.deleteFileFolder(id) }
                },
                onDismiss = { folderToDelete = null }
            )
        }
    }
}
